import React, { useEffect, useMemo, useState } from "react";
import Modal from "./Modal";
import Menu, { MenuItem } from "./Menu";
// import Slider from "./Slider"; // used by the (currently hidden) default-permit toggle
import {
  Permit,
  PermitType,
  permitService,
  CreatePermitPayload,
} from "../utils/api";
import "./PermitModal.css";

interface PermitModalProps {
  isOpen: boolean;
  lotId: string;
  permit?: Permit | null; // null/undefined => create mode
  onClose: () => void;
  onSaved: (permit: Permit) => void;
  onDeleted?: (permitId: string) => void;
}

const PERMIT_TYPE_OPTIONS: { value: PermitType; label: string }[] = [
  { value: "FREE", label: "Free Parking" },
  { value: "PERCENTAGE_DISCOUNT", label: "Percentage Discount" },
  { value: "FIXED_DISCOUNT", label: "Fixed Discount" },
];

const NAME_MAX = 30;
const DESCRIPTION_MAX = 150;

// 0 = Sunday ... 6 = Saturday (matches JS Date.getDay() and the backend storage convention)
const WEEK_DAYS: { value: number; label: string; name: string }[] = [
  { value: 0, label: "S", name: "Sunday" },
  { value: 1, label: "M", name: "Monday" },
  { value: 2, label: "T", name: "Tuesday" },
  { value: 3, label: "W", name: "Wednesday" },
  { value: 4, label: "T", name: "Thursday" },
  { value: 5, label: "F", name: "Friday" },
  { value: 6, label: "S", name: "Saturday" },
];

const CURRENCY_OPTIONS = ["CAD", "USD", "EURO", "INR", "JPY"];

// Map known backend error codes to friendly messages.
const ERROR_MESSAGES: Record<string, string> = {
  PER00001: "A permit with this name already exists for this lot.",
  PER00003: "This permit no longer exists.",
  PER00005: "Please check the permit settings and try again.",
  PER00009: "This permit is assigned to one or more plates and can't be deleted. Unassign it first.",
};

const Chevron: React.FC<{ open?: boolean }> = ({ open }) => (
  <svg
    className={`permit-select-arrow ${open ? "open" : ""}`}
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Convert an ISO/OffsetDateTime string to a yyyy-MM-dd value for <input type="date">
const isoToDateInput = (value?: string | null): string => (value ? value.slice(0, 10) : "");

const PermitModal: React.FC<PermitModalProps> = ({
  isOpen,
  lotId,
  permit,
  onClose,
  onSaved,
  onDeleted,
}) => {
  const isEdit = Boolean(permit?.permitId);

  const [permitName, setPermitName] = useState("");
  const [description, setDescription] = useState("");
  const [permitType, setPermitType] = useState<PermitType>("FREE");
  const [discountPercent, setDiscountPercent] = useState("");
  const [discountAmount, setDiscountAmount] = useState("");
  const [currency, setCurrency] = useState("CAD");
  const [validFrom, setValidFrom] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [validWeekDays, setValidWeekDays] = useState<number[]>([]);
  const [isDefault, setIsDefault] = useState(false);

  const toggleDay = (day: number) => {
    setValidWeekDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const [typeMenuOpen, setTypeMenuOpen] = useState(false);
  const [currencyMenuOpen, setCurrencyMenuOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // (Re)initialize the form whenever the modal opens or the target permit changes.
  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setTypeMenuOpen(false);
    setCurrencyMenuOpen(false);
    if (permit) {
      setPermitName(permit.permitName ?? "");
      setDescription(permit.description ?? "");
      setPermitType(permit.permitType ?? "FREE");
      setDiscountPercent(permit.discountPercent != null ? String(permit.discountPercent) : "");
      setDiscountAmount(permit.discountAmount != null ? String(permit.discountAmount) : "");
      setCurrency(permit.currency || "CAD");
      setValidFrom(isoToDateInput(permit.validFrom));
      setValidUntil(isoToDateInput(permit.validUntil));
      setValidWeekDays(permit.validWeekDays ?? []);
      setIsDefault(Boolean(permit.isDefault));
    } else {
      setPermitName("");
      setDescription("");
      setPermitType("FREE");
      setDiscountPercent("");
      setDiscountAmount("");
      setCurrency("CAD");
      setValidFrom("");
      setValidUntil("");
      setValidWeekDays([]);
      setIsDefault(false);
    }
  }, [isOpen, permit]);

  const selectedTypeLabel = useMemo(
    () => PERMIT_TYPE_OPTIONS.find(o => o.value === permitType)?.label ?? "Select type",
    [permitType]
  );

  const typeMenuItems: MenuItem[] = PERMIT_TYPE_OPTIONS.map(o => ({
    key: o.value,
    label: o.label,
    selected: permitType === o.value,
    onSelect: () => setPermitType(o.value),
  }));

  const currencyMenuItems: MenuItem[] = CURRENCY_OPTIONS.map(c => ({
    key: c,
    label: c,
    selected: currency === c,
    onSelect: () => setCurrency(c),
  }));

  const validate = (): string | null => {
    if (!permitName.trim()) return "Permit name is required.";
    if (permitType === "PERCENTAGE_DISCOUNT") {
      const pct = Number(discountPercent);
      if (!discountPercent.trim() || Number.isNaN(pct)) return "Enter a discount percentage.";
      if (pct < 0 || pct > 100) return "Discount percentage must be between 0 and 100.";
    }
    if (permitType === "FIXED_DISCOUNT") {
      const amt = Number(discountAmount);
      if (!discountAmount.trim() || Number.isNaN(amt)) return "Enter a discount amount.";
      if (amt < 0) return "Discount amount can't be negative.";
      if (!currency) return "Select a currency.";
    }
    if (validFrom && validUntil && validUntil < validFrom) {
      return "Valid Until can't be before Valid From.";
    }
    return null;
  };

  const buildPayload = (): CreatePermitPayload => {
    const base: CreatePermitPayload = {
      lotId,
      permitName: permitName.trim(),
      description: description.trim() || null,
      permitType,
      isDefault,
      // Only send a populated date as a full-day OffsetDateTime in UTC.
      validFrom: validFrom ? `${validFrom}T00:00:00Z` : null,
      validUntil: validUntil ? `${validUntil}T23:59:59Z` : null,
      validWeekDays: [...validWeekDays].sort((a, b) => a - b),
      discountPercent: null,
      discountAmount: null,
      currency: null,
    };
    if (permitType === "PERCENTAGE_DISCOUNT") {
      base.discountPercent = Number(discountPercent);
    } else if (permitType === "FIXED_DISCOUNT") {
      base.discountAmount = Number(discountAmount);
      base.currency = currency;
    }
    return base;
  };

  const friendlyError = (err: any, fallback: string): string => {
    const code: string | undefined = err?.response?.data?.message;
    if (code && ERROR_MESSAGES[code]) return ERROR_MESSAGES[code];
    return code || fallback;
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = buildPayload();
      let result: Permit;
      if (isEdit) {
        // UpdatePermitDTO has no lotId field; the backend rejects unknown properties.
        const { lotId: _omitLotId, ...updatePayload } = payload;
        result = await permitService.updatePermit(permit!.permitId, updatePayload);
      } else {
        result = await permitService.createPermit(payload);
      }
      onSaved(result);
    } catch (err) {
      setError(friendlyError(err, "Couldn't save the permit. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    setDeleting(true);
    setError(null);
    try {
      await permitService.deletePermit(permit!.permitId);
      onDeleted?.(permit!.permitId);
    } catch (err) {
      setError(friendlyError(err, "Couldn't delete the permit. Please try again."));
    } finally {
      setDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen
      title={isEdit ? "Edit Permit" : "Add Permit"}
      onCancel={onClose}
      onConfirm={handleSave}
      hideButtons
      customContent={
        <div className="permit-modal-form">
          {error && <div className="modal-error">{error}</div>}

          <div className="permit-field-row">
            <div className="permit-field permit-field-grow">
              <label>Permit Name</label>
              <input
                type="text"
                value={permitName}
                onChange={e => setPermitName(e.target.value)}
                maxLength={NAME_MAX}
                placeholder="e.g. Staff Permit"
              />
            </div>
            <div className="permit-field permit-field-grow">
              <label>Permit Type</label>
              <Menu
                open={typeMenuOpen}
                onOpenChange={setTypeMenuOpen}
                allowNoSelection={false}
                showSelectionIndicator
                trigger={
                  <button type="button" className="permit-select-trigger">
                    <span className="permit-select-value">{selectedTypeLabel}</span>
                    <Chevron open={typeMenuOpen} />
                  </button>
                }
                items={typeMenuItems}
                placement="bottom-start"
              />
            </div>
          </div>

          <div className="permit-field">
            <label>Description (optional)</label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              maxLength={DESCRIPTION_MAX}
              placeholder="Short description"
            />
          </div>

          <div className="permit-field">
            <label>Days Valid (optional)</label>
            <div className="permit-weekdays">
              {WEEK_DAYS.map((day, index) => {
                const active = validWeekDays.includes(day.value);
                return (
                  <button
                    key={index}
                    type="button"
                    className={`permit-weekday ${active ? "selected" : ""}`}
                    onClick={() => toggleDay(day.value)}
                    aria-pressed={active}
                    aria-label={day.name}
                    title={day.name}
                  >
                    {day.label}
                  </button>
                );
              })}
            </div>
          </div>

          {permitType === "PERCENTAGE_DISCOUNT" && (
            <div className="permit-field">
              <label>Discount Percentage</label>
              <div className="permit-input-suffix">
                <input
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  value={discountPercent}
                  onChange={e => setDiscountPercent(e.target.value)}
                  placeholder="0"
                />
                <span className="permit-suffix">%</span>
              </div>
            </div>
          )}

          {permitType === "FIXED_DISCOUNT" && (
            <div className="permit-field-row">
              <div className="permit-field permit-field-grow">
                <label>Discount Amount</label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={discountAmount}
                  onChange={e => setDiscountAmount(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="permit-field permit-field-currency">
                <label>Currency</label>
                <Menu
                  open={currencyMenuOpen}
                  onOpenChange={setCurrencyMenuOpen}
                  allowNoSelection={false}
                  showSelectionIndicator
                  trigger={
                    <button type="button" className="permit-select-trigger">
                      <span className="permit-select-value">{currency}</span>
                      <Chevron open={currencyMenuOpen} />
                    </button>
                  }
                  items={currencyMenuItems}
                  placement="bottom-start"
                />
              </div>
            </div>
          )}

          <div className="permit-field-row">
            <div className="permit-field permit-field-grow">
              <label>Valid From (optional)</label>
              <input type="date" value={validFrom} onChange={e => setValidFrom(e.target.value)} />
            </div>
            <div className="permit-field permit-field-grow">
              <label>Valid Until (optional)</label>
              <input type="date" value={validUntil} onChange={e => setValidUntil(e.target.value)} />
            </div>
          </div>

          {/* Default-permit toggle hidden for now — keep for potential future use.
          <div className="permit-toggle-row">
            <span className="permit-toggle-label">Set as default permit for this lot</span>
            <Slider checked={isDefault} onChange={() => setIsDefault(prev => !prev)} />
          </div>
          */}

          <div className="modal-actions-inline">
            {isEdit && (
              <button
                type="button"
                className="button-danger"
                onClick={handleDelete}
                disabled={deleting || saving}
              >
                {deleting ? "Deleting…" : "Delete"}
              </button>
            )}
            <button type="button" className="button-secondary" onClick={onClose} disabled={saving || deleting}>
              Cancel
            </button>
            <button type="button" className="button-primary" onClick={handleSave} disabled={saving || deleting}>
              {saving ? "Saving…" : isEdit ? "Save" : "Create Permit"}
            </button>
          </div>
        </div>
      }
    />
  );
};

export default PermitModal;
